import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import Toast from '../components/Toast';
import api, { formatApiErrorDetail } from '../api';

/**
 * /waitlist — user's pending, fulfilled, and failed waitlist entries.
 * Phase 8.1 — when a Clan is sold out, users can queue for entry; the trading
 * engine auto-fills them (FIFO) as holders sell.
 */
export default function Waitlist() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState(null);
  const [toast, setToast] = useState(null);

  const load = () => {
    api.get('/waitlist/mine')
      .then(({ data }) => setEntries(data.entries || []))
      .catch(() => setEntries([]));
  };

  useEffect(() => { load(); }, []);

  const leave = async (handle) => {
    try {
      await api.delete(`/clans/${handle}/waitlist/leave`);
      setToast({ msg: 'Left waitlist' });
      load();
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not leave waitlist' });
    }
  };

  if (entries === null) {
    return <AppShell><div className="loading-shell">loading…</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="wl-shell" data-testid="waitlist-page">
        <h1 className="wl-heading">Your <span>waitlist</span></h1>
        <p className="wl-sub">
          Clans you're queued for. When a holder sells, the oldest pending entry auto-fills — you get notified.
        </p>

        {entries.length === 0 && (
          <div className="wl-empty" data-testid="waitlist-empty">
            <div className="wl-empty-icon">🎟️</div>
            <div className="wl-empty-title">You're not waitlisted anywhere</div>
            <div className="wl-empty-sub">Find a sold-out Clan and join the queue.</div>
            <button className="wl-empty-btn" onClick={() => navigate('/home')} data-testid="waitlist-browse-btn">Browse Clans</button>
          </div>
        )}

        {entries.length > 0 && (
          <div className="wl-list" data-testid="waitlist-list">
            {entries.map((e) => (
              <div key={e.id} className={`wl-row wl-${e.status}`} data-testid={`waitlist-row-${e.clan_handle}`}>
                <div
                  className="wl-avatar"
                  onClick={() => navigate(`/creator/${e.clan_handle}`)}
                  style={{
                    backgroundImage: e.creator_avatar ? `url(${e.creator_avatar})` : undefined,
                  }}
                >
                  {!e.creator_avatar && (e.creator_display_name || 'U').slice(0, 1).toUpperCase()}
                </div>
                <div className="wl-body">
                  <div className="wl-name" onClick={() => navigate(`/creator/${e.clan_handle}`)}>
                    {e.creator_display_name}
                  </div>
                  <div className="wl-meta">
                    <span>@{e.clan_handle}</span>
                    <span className="wl-sep">·</span>
                    <span>{e.qty} Bit{e.qty === 1 ? '' : 's'} requested</span>
                    {e.status === 'pending' && e.position && (
                      <>
                        <span className="wl-sep">·</span>
                        <span className="wl-pos" data-testid={`waitlist-pos-${e.clan_handle}`}>Position #{e.position}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="wl-status">
                  {e.status === 'pending' && (
                    <>
                      <span className="wl-pill pending">Pending</span>
                      <button className="wl-leave-btn" onClick={() => leave(e.clan_handle)} data-testid={`waitlist-leave-${e.clan_handle}`}>Leave</button>
                    </>
                  )}
                  {e.status === 'fulfilled' && <span className="wl-pill fulfilled">✓ Filled</span>}
                  {e.status === 'failed_balance' && <span className="wl-pill failed" title="Insufficient balance when a spot opened">Missed — low balance</span>}
                  {e.status === 'failed_other' && <span className="wl-pill failed">Failed</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AppShell>
  );
}
